<template>
  <ComponentState :fetch-status="portfolioStatus">
    <template #loading>
      <TableSkeleton :row-number="10" :column-number="8" />
    </template>

    <template #error>
      <Error
        @retry="
          () => queryClient.invalidateQueries(PORTFOLIO_QUERY_KEYS.portfolio)
        "
      />
    </template>

    <NTable
      v-if="portfolioSymbols.length"
      ref="tableRef"
      :data="portfolioSymbols"
      :sort-by="{
        prop: 'value',
        order: 'descending',
      }"
      :highlight-by="{
        prop: 'instrumentIsin',
        value: activeIsin,
      }"
      :striped="false"
      cell-padding="small"
      :column-splitter="false"
      :class="{
        '!scroll-auto': portfolioSymbols.length > MAX_SYMBOL_LENGTH_FOR_MOTION,
      }"
      class="border-none rounded-none"
      hover-alignment="right"
      hover-offset="10%"
      virtual-scroll
      index-key="instrumentIsin"
      identifier="instrumentIsin"
      fixed-header
      @click.stop
      @row-click="(row) => updateActiveIsin(row.instrumentIsin)"
    >
      <template #hoveredElements="{ row }">
        <div class="flex items-center h-full gap-2 pe-24 ps-8">
          <span
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'portfolio',
              component: 'editButton',
            }"
            class="ml-1 p-1 active:bg-neutral-secondary-hover hover:bg-neutral-secondary rounded-full"
            @click.stop="
              openEditNetAveragePriceDialog({
                selectedSymbol: row,
                referenceElementRef: $event.target as HTMLElement,
              })
            "
          >
            <NegarEditBorderlessIcon size="20" />
          </span>

          <NButton
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'portfolio',
              component: 'buyButton',
            }"
            variant="fill"
            size="mini"
            @click.stop="
              openNewOrderFormDialog({
                type: ORDER_TYPES.buy,
                symbol: row,
              })
            "
          >
            <NText weight="medium" variant="overline">
              {{ t('common.buy') }}
            </NText>
          </NButton>

          <NButton
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'portfolio',
              component: 'sellButton',
            }"
            variant="fill"
            color="red"
            size="mini"
            class="w-[100px]"
            @click.stop="
              openNewOrderFormDialog({
                type: ORDER_TYPES.sell,
                quantity: row.quantity,
                isLinkToPrice: true,
                symbol: row,
              })
            "
          >
            <NText weight="medium" variant="overline">
              {{ t('symbol.sellPortfolio') }}
            </NText>
          </NButton>

          <NButton
            v-if="row.falconAlgorithmInfo?.isSLTP && isSltpFeatureFlag"
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'portfolio',
              component: 'SLTPDetailButton',
            }"
            variant="outline"
            color="gray"
            size="mini"
            @click.stop="
              openSLTPInfoDialog({
                instrumentName: row.instrumentName,
                order: row,
                from: SLTP_DETAIL.portfolio,
              })
            "
          >
            <NText weight="medium" variant="overline">
              {{ t('modules.order.SLTPDetails') }}
            </NText>
          </NButton>

          <NButton
            v-else-if="isSltpFeatureFlag"
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'portfolio',
              component: 'SLTPButton',
            }"
            variant="outline"
            color="red"
            size="mini"
            @click.stop="
              openCreateSLTPOrderDialog({
                instrumentName: row.instrumentName,
                symbol: row,
              })
            "
          >
            <NText weight="medium" variant="overline">
              {{ t('modules.order.SLTP') }}
            </NText>
          </NButton>
        </div>
      </template>

      <NTableColumn
        v-slot="{ row }"
        :label="t('modules.portfolio.symbol')"
        prop="instrumentName"
        sortable
        align="text-right"
      >
        <div class="flex flex-col justify-between items-start">
          <span class="inline-flex items-center gap-1">
            <SymbolTradeStateIcon
              class="me-1"
              :trading-state-code="row.instrumentTradingState"
            />
            <NText :key="row.instrumentName" variant="caption2" weight="medium">
              {{ row.instrumentName }}
            </NText>
            <NPopper
              v-if="row.collateralQuantity > 0"
              hover
              offset-y="5"
              placement="top"
              append-to-body
            >
              <NButtonIcon
                v-gid="{
                  module: 'home',
                  screen: 'home',
                  section: 'portfolioTable',
                  component: `portfolioTableTosigh`,
                }"
                size="mini"
                color="gray"
              >
                <NegarLockIcon />
              </NButtonIcon>
              <template #content>
                <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                  <NText
                    class="text-neutral-quaternary"
                    variant="caption2"
                    weight="medium"
                  >
                    {{
                      t('modules.portfolio.collateralQuantity', {
                        collateralQuantity: row.collateralQuantity,
                      })
                    }}
                  </NText>
                </div>
              </template>
            </NPopper>
          </span>

          <!-- Bonus Share -->
          <span
            v-if="Boolean(row?.bonusShare)"
            class="inline-flex items-center"
          >
            <div
              class="rounded-full bg-neutral-quinary flex items-center justify-center me-1 p-0.5"
            >
              <NegarGiftIcon class="fill-[#FAB60E]" size="7" />
            </div>
            <NText class="text-neutral-80" variant="caption2" weight="medium">
              {{ t('modules.portfolio.bonusShare') }}
            </NText>
          </span>
        </div>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('modules.portfolio.quantity')"
        prop="quantity"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.quantity">
          <ChangePriceEffect :key="row.quantity" :price="row.quantity">
            <NText class="block" variant="caption2" weight="medium">
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>

        <!-- Bonus Share -->
        <NumberFormatter
          v-if="Boolean(row?.bonusShare)"
          #="{ number }"
          :value="row.bonusShare?.quantity"
        >
          <ChangePriceEffect
            :key="row.instrumentIsin"
            :price="row.bonusShare?.quantity"
          >
            <NText
              class="block text-neutral-80"
              variant="caption2"
              weight="medium"
            >
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="NetAveragePriceLabel"
        prop="netAverageDps"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.netAverageDps" round="up">
          <NText
            :key="row.netAverageDps"
            class="block"
            dir="ltr"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>

        <!-- Bonus Share -->
        <NumberFormatter
          v-if="Boolean(row?.bonusShare)"
          :value="row?.bonusShare?.netAverageDps"
          round="up"
        >
          <NText
            :key="row?.bonusShare?.netAverageDps"
            class="block text-neutral-80"
            dir="ltr"
            variant="caption2"
            weight="medium"
          >
            -
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="TotalBuyLabel"
        prop="symbolBuyValue"
        sortable
        align="text-right"
      >
        <NumberFormatter
          #="{ number }"
          :value="
            row.symbolBuyValue + (row.bonusShare?.bonusShareBuyValue || 0)
          "
          round="up"
        >
          <NText
            :key="row.symbolBuyValue"
            dir="ltr"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('modules.portfolio.break_point')"
        prop="breakPoint"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.breakPoint" round="up">
          <NText
            :key="row.breakPoint"
            dir="ltr"
            class="block"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>

        <!-- Bonus Share -->
        <NText
          v-if="Boolean(row?.bonusShare)"
          dir="ltr"
          class="block text-neutral-80"
          variant="caption2"
          weight="medium"
          >-</NText
        >
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('modules.portfolio.current_price')"
        prop="lastTradePrice"
        sortable
        align="text-right"
        class="w-32"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.referencePriceVariationPercentage / 100"
          maximum-fraction-digits="2"
          sign-display="exceptZero"
          number-style="percent"
        >
          <NText
            :key="row.referencePriceVariationPercentage"
            dir="ltr"
            variant="caption2"
            weight="medium"
            class="ps-1"
            :class="{
              'text-brand-primary': row.referencePriceVariationPercentage > 0,
              'text-critical-primary':
                row.referencePriceVariationPercentage < 0,
              'text-neutral-secondary':
                row.referencePriceVariationPercentage === 0,
            }"
          >
            ({{ number }})
          </NText>
        </NumberFormatter>

        <NumberFormatter #="{ number }" :value="row.lastTradePrice">
          <ChangePriceEffect :price="row.lastTradePrice">
            <NText
              :key="row.lastTradePrice"
              dir="ltr"
              variant="caption2"
              weight="medium"
            >
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('misc.value')"
        prop="value"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.value" round="down">
          <NText
            :key="row.value"
            class="block"
            dir="ltr"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>

        <!-- Bonus Share -->
        <NumberFormatter
          v-if="Boolean(row?.bonusShare)"
          #="{ number }"
          :value="row.bonusShare?.bonusShareSellValue"
          round="down"
        >
          <NText
            :key="row.bonusShare?.bonusShareSellValue"
            class="block text-neutral-80"
            dir="ltr"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="`${t('modules.portfolio.profit')}/${t(
          'modules.portfolio.loss'
        )}`"
        prop="returnValue"
        sortable
        align="text-right"
      >
        <div :key="row.returnValuePercentage" class="flex items-center">
          <div class="flex flex-col justify-start items-stretch">
            <div class="flex justify-start items-center grow">
              <NumberFormatter
                #="{ number }"
                :value="row.returnValuePercentage / 100"
                number-style="percent"
                maximum-fraction-digits="2"
                minimum-fraction-digits="2"
                sign-display="exceptZero"
              >
                <NText
                  class="align-middle"
                  dir="ltr"
                  :class="{
                    'text-brand-primary': row.returnValuePercentage > 0,
                    'text-critical-primary': row.returnValuePercentage < 0,
                    'text-neutral-secondary': row.returnValuePercentage === 0,
                  }"
                  variant="caption2"
                  weight="medium"
                >
                  ({{ number }})
                </NText>
              </NumberFormatter>

              <NumberFormatter
                #="{ number }"
                :value="row.returnValue"
                sign-display="exceptZero"
                round="down"
              >
                <NText
                  class="align-middle mx-1 transition-all duration-300"
                  :class="{
                    'text-brand-primary': row.returnValuePercentage > 0,
                    'text-critical-primary': row.returnValuePercentage < 0,
                    'text-neutral-secondary': row.returnValuePercentage === 0,
                  }"
                  variant="caption2"
                  weight="medium"
                  dir="ltr"
                >
                  {{ number }}
                </NText>
              </NumberFormatter>
            </div>

            <!-- Bonus Share -->
            <div
              v-if="Boolean(row?.bonusShare)"
              class="flex justify-start items-center grow"
            >
              <NumberFormatter
                #="{ number }"
                :value="row.bonusShare?.returnBonusSharePercentage / 100"
                number-style="percent"
                maximum-fraction-digits="2"
                minimum-fraction-digits="2"
                sign-display="exceptZero"
              >
                <NText
                  class="align-middle"
                  dir="ltr"
                  :class="{
                    'text-brand-primary':
                      row.bonusShare?.returnBonusSharePercentage > 0,
                    'text-critical-primary':
                      row.bonusShare?.returnBonusSharePercentage < 0,
                    'text-neutral-secondary':
                      row.bonusShare?.returnBonusSharePercentage === 0,
                  }"
                  variant="caption2"
                  weight="medium"
                >
                  ({{ number }})
                </NText>
              </NumberFormatter>

              <NumberFormatter
                #="{ number }"
                :value="row.bonusShare?.returnBonusShareValue"
                sign-display="exceptZero"
                round="down"
              >
                <NText
                  class="align-middle mx-1 transition-all duration-300"
                  :class="{
                    'text-brand-primary':
                      row.bonusShare?.returnBonusSharePercentage > 0,
                    'text-critical-primary':
                      row.bonusShare?.returnBonusSharePercentage < 0,
                    'text-neutral-secondary':
                      row.bonusShare?.returnBonusSharePercentage === 0,
                  }"
                  variant="caption2"
                  weight="medium"
                  dir="ltr"
                >
                  {{ number }}
                </NText>
              </NumberFormatter>
            </div>
          </div>

          <div class="flex mr-auto">
            <NButtonIcon
              v-if="row.dividend"
              v-gid="{
                module: 'portfolio',
                screen: 'home',
                section: 'portfolioTable',
                component: 'lastCashDividedBtn',
              }"
              size="mini"
              class="me-1"
              color="gray"
              variant="flat"
              @click.stop="
                openDividendDialog({
                  selectedSymbol: row,
                  referenceElementRef: $event.target,
                })
              "
            >
              <NegarAchievementIcon class="text-brand-primary" size="16" />
            </NButtonIcon>

            <template
              v-if="row.falconAlgorithmInfo?.isSLTP && isSltpFeatureFlag"
            >
              <SLTPTooltip :falcon-info="row.falconAlgorithmInfo" />
            </template>
          </div>
        </div>
      </NTableColumn>
    </NTable>

    <div
      v-else
      class="bg-neutral-primary flex items-center justify-center overflow-y-auto w-full h-full"
    >
      <EmptyList
        :title="t('modules.portfolio.emptyPortfolioTitle')"
        :body="t('modules.portfolio.emptyPortfolioMessage')"
        title-class="text-center mt-3"
        wrapper-class="w-96 flex flex-col items-center justify-center"
        class="h-auto flex flex-col m-auto items-center justify-center p-2 bg-neutral-primary rounded-md"
      >
        <template #image>
          <Image src="empty_box" alt="not_found" class="w-28 h-max" />
        </template>

        <NButton
          v-gid="{
            module: 'portfolio',
            screen: 'home',
            section: 'portfolioTable',
            component: 'brokerChangeButton',
          }"
          variant="flat"
          color="green"
          class="px-6"
          @click="openBrokerChangeDialog"
        >
          <NText weight="bold" variant="caption1">
            {{ t('modules.portfolio.brokerChangeTitle') }}
          </NText>
        </NButton>
      </EmptyList>
    </div>
  </ComponentState>
</template>

<script setup lang="ts">
import { h, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ORDER_TYPES } from '@core/order';
import { PORTFOLIO_QUERY_KEYS } from '@core/portfolio';
import {
  NegarEditBorderlessIcon,
  NegarInfoWithBorderIcon,
  NegarAchievementIcon,
  NegarGiftIcon,
  NegarLockIcon,
} from '@negar/icons';
import { NText } from '@negar/core';
import { PortfolioItem } from '@libs/api-reference';
import { useFeatureFlag } from '@libs/feature-flag';
import { QueryStatus, useQueryClient } from '@libs/use';

import {
  SymbolTradeStateIcon,
  useActiveSymbolIsin,
  useSymbolDialogs,
} from '@/modules/symbol';
import { useOrderDialogs, SLTP_DETAIL, SLTPTooltip } from '@/modules/order';
import { usePortfolioDialogs } from '@/modules/portfolio';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import EmptyList from '@/components/EmptyList.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';
import Error from '@/components/Error.vue';
import ComponentState from '@/components/ComponentState.vue';
import Image from '@/components/Image.vue';

type PortfolioTableProps = {
  portfolioSymbols: PortfolioItem[];
  portfolioStatus: QueryStatus;
};

defineProps<PortfolioTableProps>();

const { t } = useI18n();
const isSltpFeatureFlag = useFeatureFlag('sltp');

const queryClient = useQueryClient();

const {
  openNetAveragePriceInfoDialog,
  openTotalBuyInfoDialog,
  openDividendDialog,
  openEditNetAveragePriceDialog,
} = usePortfolioDialogs();
const {
  openNewOrderFormDialog,
  openCreateSLTPOrderDialog,
  openSLTPInfoDialog,
} = useOrderDialogs();
const { openBrokerChangeDialog } = useSymbolDialogs();

const MAX_SYMBOL_LENGTH_FOR_MOTION = 50;

const tableRef = ref();

const { activeIsin, updateActiveIsin } = useActiveSymbolIsin();

const NetAveragePriceLabel = h(
  'span',
  {
    class: 'inline-flex items-center align-middle',
  },
  [
    h(NegarInfoWithBorderIcon, {
      size: 14,
      class: 'me-0.5',
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        openNetAveragePriceInfoDialog();
      },
    }),
    h(
      NText,
      {
        weight: 'bold',
        variant: 'overline',
      },
      [t('modules.portfolio.net_average')]
    ),
  ]
);

const TotalBuyLabel = h(
  'span',
  {
    class: 'inline-flex items-center align-middle',
  },
  [
    h(NegarInfoWithBorderIcon, {
      size: 14,
      class: 'me-0.5',
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        openTotalBuyInfoDialog();
      },
    }),
    h(
      NText,
      {
        weight: 'bold',
        variant: 'overline',
      },
      [t('modules.portfolio.total_buy')]
    ),
  ]
);

function scrollToActiveIsin() {
  tableRef.value.virtualScrollTo({
    prop: 'instrumentIsin',
    value: activeIsin.value,
  });
}

onMounted(() => {
  watch(
    [activeIsin, tableRef],
    () => {
      if (activeIsin.value && tableRef.value) {
        scrollToActiveIsin();
      }
    },
    {
      immediate: true,
    }
  );
});
</script>
